<script setup lang="ts">
  import { PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3 } from '@/shared/constants/plans.constants'
  import { type Plan, SchemeType } from '@/shared/types/api.types'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { PLAN_ANNOTATION_SCHEME_MAPPING } from '../../FlowSelectPlanWidget.constants'

  interface Props {
    plan: Plan
    plansSchemeTypeCodes: SchemeType[]
  }

  interface Emits {
    (e: 'select-plan', plan: Plan): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const { t } = useI18n()

  const schemeTypeCode = computed(() => props.plan.schemeTypeCode)

  const hasType = (type: SchemeType) => props.plansSchemeTypeCodes.includes(type)

  const isRecommended = computed(() => {
    if (schemeTypeCode.value === SchemeType.Worldwide) return true
    return schemeTypeCode.value === SchemeType.WorldwideExcl && !hasType(SchemeType.Worldwide)
  })

  const title = computed(() => {
    const key =
      schemeTypeCode.value === SchemeType.WorldwideExcl && hasType(SchemeType.Worldwide)
        ? PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3[SchemeType.WorldwideExcl]
        : PLAN_NAMES_SCHEME_TYPE_CODE_MAP_V3[schemeTypeCode.value]

    return t(key ?? '')
  })

  const annotation = computed(() => t(PLAN_ANNOTATION_SCHEME_MAPPING[schemeTypeCode.value]))

  const handleSelectPlan = () => {
    emit('select-plan', props.plan)
  }
</script>

<template>
  <div class="floating-plan" :class="{ recommended: isRecommended }">
    <div class="floating-plan__content">
      <div>
        <div class="floating-plan__title heading-6">
          {{ title }} - {{ $t('qar_price', { price: plan.price }) }}
        </div>
        <div class="floating-plan__annotation text-regular">{{ annotation }}</div>
      </div>
      <ButtonQC
        :variant="isRecommended ? 'primary' : 'secondary'"
        class="floating-plan__select-button"
        size="small"
        @click="handleSelectPlan"
        >{{ $t('travel.outbound.select_plan.select_button') }}</ButtonQC
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .floating-plan {
    border-radius: var(--spacing-5);
    border: 2px solid var(--colorBgSrfTertiary);

    &.recommended {
      border: none;
      background: linear-gradient(
        107deg,
        var(--colorAccentPurple700, #6568f4) 0%,
        var(--colorAccentPurple400, #c5ceff) 100%
      );
      display: flex;
      align-items: center;
      justify-content: center;

      .floating-plan__title {
        background: linear-gradient(
          135deg,
          var(--colorAccentPurple700) 0%,
          var(--colorTxtSrfAction) 100%
        );
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .floating-plan__content {
        width: calc(100% - 4px);
        height: calc(100% - 4px);
      }
    }

    .floating-plan__content {
      padding: var(--spacing-6);
      background-color: var(--colorBgSrfPrimary);
      gap: var(--spacing-4);

      /* qic-connect-ignore-next-line */
      border-radius: 14px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 100%;
      width: 100%;
    }

    .floating-plan__select-button {
      width: 140px;
    }
  }

  .floating-plan__annotation {
    color: var(--colorTxtSrfSecondary);
  }
</style>
